import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Injector,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { QuestionarioService } from '../../core/services/questionario.service';
import { LoaderService } from '../../shared/services/loader/loader.service';
import { ToastService } from '../../core/services/toast.service';
import { Questionario, QuestionarioFiltros } from '../../core/models/questionario.model';
import { QuestionarioStatus } from '../../core/enum/questionario-status.enum';
import { StatusBadgeComponent } from '../../shared/components/status-badge/status-badge.component';
import { DataTableComponent } from '../../shared/components/data-table/data-table.component';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';
import { AlertDialogComponent } from '../../shared/components/alert-dialog/alert-dialog.component';
import { BackButtonComponent } from '../../shared/components/back-button/back-button.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { FilterBarComponent, FilterField } from '../../shared/components/filter-bar/filter-bar.component';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { ApiHttpError } from '../../shared/services/http-client/api-http-error.model';
import {
  ActionsMenuPosition,
  adjustActionsMenuPosition,
  getActionsMenuPosition,
} from '../../shared/utils/actions-menu.util';

function formatDateTimeToBr(isoDateTime: string): string {
  if (!isoDateTime) return '';
  const datePart = isoDateTime.split('T')[0];
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return isoDateTime;
  return `${day}/${month}/${year}`;
}

const PAGE_SIZE = 10;

@Component({
  selector: 'app-questionarios-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, BackButtonComponent, PageHeaderComponent, FilterBarComponent, DataTableComponent, StatusBadgeComponent, EmptyStateComponent, AlertDialogComponent, PaginationComponent],
  templateUrl: './questionarios-list.component.html',
  styleUrl: './questionarios-list.component.scss',
})
export class QuestionariosListComponent {
  private readonly questionarioService = inject(QuestionarioService);
  private readonly router = inject(Router);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly injector = inject(Injector);

  readonly questionarios = signal<readonly Questionario[]>([]);
  readonly currentPage = signal(1);
  readonly pageSize = PAGE_SIZE;
  readonly menuAbertoId = signal<number | null>(null);
  readonly menuPosition = signal<ActionsMenuPosition | null>(null);
  readonly showDeleteDialog = signal(false);
  readonly questionarioPendenteId = signal<number | null>(null);
  readonly formatDate = formatDateTimeToBr;

  /** Configuração dos campos do filtro compartilhado (`app-filter-bar`). */
  readonly filterFields: FilterField[] = [
    {
      key: 'nome',
      label: 'Procure por nome',
      type: 'text',
      placeholder: 'Digite o nome do questionário',
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: '', label: 'Todos' },
        { value: QuestionarioStatus.Rascunho, label: 'Rascunho' },
        { value: QuestionarioStatus.Publicado, label: 'Publicado' },
      ],
    },
    { key: 'data', label: 'Data', type: 'date' },
  ];

  private readonly filtros = signal<Record<string, string>>({});

  readonly questionariosFiltrados = computed(() => {
    const f = this.filtros();
    return this.questionarios().filter((q) => {
      const nomeOk = !f['nome'] || q.nome.toLowerCase().includes(f['nome'].toLowerCase());
      const statusOk = !f['status'] || q.status === f['status'];
      const dataOk = !f['data'] || q.ultimaAtualizacao.startsWith(f['data']);
      return nomeOk && statusOk && dataOk;
    });
  });

  readonly questionariosPaginados = computed(() => {
    const inicio = (this.currentPage() - 1) * PAGE_SIZE;
    return this.questionariosFiltrados().slice(inicio, inicio + PAGE_SIZE);
  });

  onFiltersChange(record: Record<string, string>): void {
    this.filtros.set(record);
    this.currentPage.set(1);

    // Nome/status são filtrados server-side (a API aceita esses params); data
    // permanece client-side via `questionariosFiltrados`.
    this.carregar({
      nome: record['nome'] || undefined,
      status: (record['status'] as QuestionarioStatus) || '',
    });
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
    this.menuAbertoId.set(null);
    this.menuPosition.set(null);
  }

  private carregar(filtros: QuestionarioFiltros): void {
    this.loaderService.showLoader();
    this.questionarioService
      .listarQuestionarios(filtros)
      .pipe(finalize(() => this.loaderService.hideLoader()))
      .subscribe({
        next: (lista) => {
          this.questionarios.set(lista);
          this.ajustarPaginaAtual();
        },
        error: (error: unknown) => {
          this.questionarios.set([]);
          this.ajustarPaginaAtual();
          const message =
            error instanceof ApiHttpError ? error.message : 'Não foi possível carregar os questionários.';
          this.toastService.show(message, 'error');
        },
      });
  }

  /** Garante que a página atual continue válida após filtros/exclusões. */
  private ajustarPaginaAtual(): void {
    const totalPaginas = Math.max(1, Math.ceil(this.questionariosFiltrados().length / PAGE_SIZE));
    if (this.currentPage() > totalPaginas) {
      this.currentPage.set(totalPaginas);
    }
  }

  voltar(): void {
    void this.router.navigate(['/']);
  }

  onAdicionar(): void {
    void this.router.navigate(['/questionarios/novo']);
  }

  toggleMenu(id: number, event: Event): void {
    event.stopPropagation();

    if (this.menuAbertoId() === id) {
      this.menuAbertoId.set(null);
      this.menuPosition.set(null);
      return;
    }

    this.menuAbertoId.set(id);
    this.menuPosition.set(getActionsMenuPosition(event));
    this.adjustMenuPosition();
  }

  private adjustMenuPosition(): void {
    afterNextRender(
      () => {
        const adjusted = adjustActionsMenuPosition(this.menuPosition());
        if (adjusted) {
          this.menuPosition.set(adjusted);
        }
      },
      { injector: this.injector },
    );
  }

  irParaEditar(id: number): void {
    this.menuAbertoId.set(null);
    void this.router.navigate(['/questionarios', id, 'editar']);
  }

  abrirConfirmacaoExclusao(id: number, event: Event): void {
    event.stopPropagation();
    this.menuAbertoId.set(null);
    this.questionarioPendenteId.set(id);
    this.showDeleteDialog.set(true);
  }

  cancelarExclusao(): void {
    this.showDeleteDialog.set(false);
    this.questionarioPendenteId.set(null);
  }

  confirmarExclusao(): void {
    const id = this.questionarioPendenteId();
    if (id === null) return;
    this.showDeleteDialog.set(false);
    this.questionarioPendenteId.set(null);
    this.questionarioService.excluirQuestionario(id).subscribe({
      next: () => {
        this.questionarios.update((lista) => lista.filter((q) => q.id !== id));
        this.ajustarPaginaAtual();
        this.toastService.show('Questionário excluído com sucesso.', 'success');
      },
      error: (error: unknown) => {
        const message =
          error instanceof ApiHttpError ? error.message : 'Não foi possível excluir o questionário.';
        this.toastService.show(message, 'error');
      },
    });
  }

  @HostListener('document:click')
  closeMenu(): void {
    this.menuAbertoId.set(null);
    this.menuPosition.set(null);
  }
}
